import { useState } from 'react';
import { Typography, Tabs, Card, Button, message, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FolderAddOutlined } from '@ant-design/icons';
import { 
  ResidentDocumentsTab, 
  EmployeeDocumentsTab,
  DocumentDetailTab 
} from '@/components/tab';
import FolderBrowser from '@/components/FolderBrowser';
import { 
  checkWorkspaceFolderStructure, 
  createWorkspaceFolderStructure 
} from '@/api/documents';
import { useAuth } from '@/contexts/AuthContext';

const { Title } = Typography;
const { TabPane } = Tabs;

export default function Documents() {
  const { t } = useTranslation(['documents', 'common']);
  const { firestoreUser } = useAuth();
  const queryClient = useQueryClient();


  const [selectedOwner, setSelectedOwner] = useState<{
    id: string;
    name: string;
    type: 'resident' | 'employee';
  } | null>(null);

  // Check if folder structure exists
  const { data: hasFolderStructure, isLoading: checkingStructure } = useQuery({
    queryKey: ['workspaceFolderStructure', firestoreUser?.workspace?.id, firestoreUser?.workspace?.slug],
    queryFn: () => checkWorkspaceFolderStructure(
      firestoreUser?.workspace?.id || '',
      firestoreUser?.workspace?.slug || ''
    ),
    enabled: !!firestoreUser?.workspace?.id && !!firestoreUser?.workspace?.slug,
  });

  // Create folder structure mutation
  const createFolderStructureMutation = useMutation({
    mutationFn: () => {
      return createWorkspaceFolderStructure(
        firestoreUser?.workspace?.id || '',
        firestoreUser?.workspace?.slug || '',
        firestoreUser?.workspace?.language || 'en' // Use workspace language or fallback to English
      );
    },
    onSuccess: () => {
      message.success(t('documents:generate.success'));
      queryClient.invalidateQueries({ queryKey: ['workspaceFolderStructure'] });
    },
    onError: (error) => {
      console.error('Error creating folder structure:', error);
      message.error(t('documents:generate.error'));
    },
  });

  const handleViewDocuments = (ownerId: string, ownerName: string, ownerType: 'resident' | 'employee') => {
    setSelectedOwner({ id: ownerId, name: ownerName, type: ownerType });
  };

  const handleBackToTabs = () => {
    setSelectedOwner(null);
  };

  const handleGenerateDocuments = () => {
    createFolderStructureMutation.mutate();
  };

  if (selectedOwner) {
    return (
      <div className="p-6">
        <Title level={2}>
          {t('documents:documentsFor')} {selectedOwner.name}
        </Title>
        <DocumentDetailTab
          ownerId={selectedOwner.id}
          ownerName={selectedOwner.name}
          ownerType={selectedOwner.type}
          onBack={handleBackToTabs}
        />
      </div>
    );
  }

  // Show loading state while checking folder structure
  if (checkingStructure) {
    return (
      <div className="p-6">
        <Card>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px' }}>
              {t('documents:checking')}
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Show generate documents button if no folder structure exists
  if (!hasFolderStructure) {
    return (
      <div className="p-6">
        <Card>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <FolderAddOutlined style={{ fontSize: '64px', color: '#1890ff', marginBottom: '16px' }} />
            <Title level={3}>{t('documents:generate.title')}</Title>
            <div style={{ marginBottom: '24px', color: '#666' }}>
              {t('documents:generate.description')}
            </div>
            <Button
              type="primary"
              size="large"
              icon={<FolderAddOutlined />}
              loading={createFolderStructureMutation.isPending}
              onClick={handleGenerateDocuments}
            >
              {t('documents:generate.button')}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Show normal document management interface
  return (
    <div className="p-6">
      <Tabs defaultActiveKey="administration" size="large">
        <TabPane 
          tab={t('documents:tabs.administration')} 
          key="administration"
        >
          <FolderBrowser />
        </TabPane>
        
        <TabPane 
          tab={t('documents:tabs.residents')} 
          key="residents"
        >
          <ResidentDocumentsTab onViewDocuments={handleViewDocuments} />
        </TabPane>
        
        <TabPane 
          tab={t('documents:tabs.employees')} 
          key="employees"
        >
          <EmployeeDocumentsTab onViewDocuments={handleViewDocuments} />
        </TabPane>
      </Tabs>
    </div>
  );
} 